import { useEffect, useMemo, useState, type ComponentProps } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import { alertStatusLabel } from "@/machines/utils/alert-status-label.ts";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { ChevronDown, X } from "lucide-react";
import { useGetClassificationTaxonomy } from "@/ml-exports/api/queries/taxonomy.queries.ts";
import { ALERT_TYPES, AlertStatus, alertTypeLabel } from "@/alerts/types/machine-alert.ts";
import { Badge } from "@/ui/components/ui/badge.tsx";
import { Button } from "@/ui/components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/ui/components/ui/dropdown-menu.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/components/ui/popover.tsx";
import { cn } from "@/ui/lib/utils.ts";

export type AlertOrdering = "-timestamp" | "timestamp";

export interface AlertsFilterState {
  type_in?: string[];
  status_in?: AlertStatus[];
  label_in?: string[];
  min_confidence?: number;
  max_confidence?: number;
  ordering: AlertOrdering;
}

interface AlertsFilterBarProps {
  value: AlertsFilterState;
  onChange: (next: AlertsFilterState) => void;
  onClear: () => void;
}

interface FilterOption<T extends string> {
  label: string;
  value: T;
}

interface MultiSelectFilterProps<T extends string> {
  label: string;
  options: FilterOption<T>[];
  value?: T[];
  onChange: (value: T[] | undefined) => void;
}

const CONFIDENCE_RANGE: readonly [number, number] = [0, 1];
const CONFIDENCE_STEP = 0.05;
const orderingOptions = (
  t: TFunction<"machines">,
): ReadonlyArray<{ label: string; value: AlertOrdering }> => [
  { label: t("alertsFilter.ordering.newest"), value: "-timestamp" },
  { label: t("alertsFilter.ordering.oldest"), value: "timestamp" },
];

const TYPE_OPTIONS: FilterOption<string>[] = ALERT_TYPES.map((type) => ({
  label: alertTypeLabel(type),
  value: type,
}));

const statusOptions = (
  t: TFunction<"machines">,
): FilterOption<AlertStatus>[] =>
  Object.values(AlertStatus).map((status) => ({
    label: alertStatusLabel(status, t),
    value: status,
  }));

const normalizeArray = <T extends string,>(value?: T[]) => {
  if (!value || value.length === 0) return undefined;

  const seen = new Set<T>();
  const normalized = value.filter((item) => {
    if (!item || seen.has(item)) return false;
    seen.add(item);
    return true;
  });

  return normalized.length > 0 ? normalized : undefined;
};

const areArraysEqual = <T extends string,>(left?: T[], right?: T[]) => {
  const normalizedLeft = normalizeArray(left) ?? [];
  const normalizedRight = normalizeArray(right) ?? [];

  if (normalizedLeft.length !== normalizedRight.length) return false;

  const rightSet = new Set(normalizedRight);

  return normalizedLeft.every((item) => rightSet.has(item));
};

const toggleValue = <T extends string,>(current: T[], value: T, checked: boolean) => {
  if (checked) {
    return current.includes(value) ? current : [...current, value];
  }

  return current.filter((item) => item !== value);
};

const clampConfidence = (value: number) => Math.min(1, Math.max(0, value));

const roundConfidence = (value: number) => Math.round(value * 100) / 100;

const normalizeConfidenceValue = (value?: number) =>
  typeof value === "number" && Number.isFinite(value) ? roundConfidence(clampConfidence(value)) : undefined;

const normalizeConfidencePair = (min?: number, max?: number) => {
  const normalizedMin = normalizeConfidenceValue(min);
  const normalizedMax = normalizeConfidenceValue(max);

  if (normalizedMin === undefined && normalizedMax === undefined) return undefined;

  if (normalizedMin !== undefined && normalizedMax !== undefined) {
    const orderedMin = Math.min(normalizedMin, normalizedMax);
    const orderedMax = Math.max(normalizedMin, normalizedMax);

    if (orderedMin === CONFIDENCE_RANGE[0] && orderedMax === CONFIDENCE_RANGE[1]) return undefined;

    return [orderedMin, orderedMax] as [number, number];
  }

  return [
    normalizedMin ?? CONFIDENCE_RANGE[0],
    normalizedMax ?? CONFIDENCE_RANGE[1],
  ] as [number, number];
};

const formatConfidence = (value: number) =>
  formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getConfidenceTriggerLabel = (
  value: AlertsFilterState,
  t: TFunction<"machines">,
) => {
  const normalized = normalizeConfidencePair(value.min_confidence, value.max_confidence);

  if (!normalized) return t("alertsFilter.confidence.label");

  return t("alertsFilter.confidence.range", {
    min: formatConfidence(normalized[0]),
    max: formatConfidence(normalized[1]),
  });
};

const buildConfidencePatch = (range: [number, number]) => {
  const normalized = normalizeConfidencePair(range[0], range[1]);

  return {
    min_confidence: normalized?.[0],
    max_confidence: normalized?.[1],
  };
};

const getSummaryLabel = <T extends string,>(
  label: string,
  value: T[] | undefined,
  options: FilterOption<T>[],
  t: TFunction<"machines">
) => {
  const normalized = normalizeArray(value);

  if (!normalized) return label;
  if (normalized.length === 1) {
    return t("alertsFilter.summary", {
      label,
      value: options.find((option) => option.value === normalized[0])?.label ?? normalized[0],
    });
  }

  return t("alertsFilter.summary", { label, value: normalized.length });
};

const FilterButton = ({
  active,
  children,
  className,
  ...props
}: ComponentProps<typeof Button> & { active?: boolean }) => (
  <Button
    variant="outline"
    size="sm"
    className={cn(
      "h-8 justify-between gap-2 px-3 text-xs",
      active && "border-primary/50 bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary",
      className
    )}
    {...props}
  >
    {children}
  </Button>
);

const MultiSelectFilter = <T extends string,>({
  label,
  options,
  value,
  onChange,
}: MultiSelectFilterProps<T>) => {
  const { t } = useTranslation("machines");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<T[]>(value ?? []);

  useEffect(() => {
    if (open) {
      setDraft(value ?? []);
    }
  }, [open, value]);

  const activeValue = normalizeArray(value);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      const normalizedDraft = normalizeArray(draft);
      if (!areArraysEqual(normalizedDraft, activeValue)) {
        onChange(normalizedDraft);
      }
    }

    setOpen(next);
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <FilterButton active={!!activeValue}>
          <span className="truncate">{getSummaryLabel(label, value, options, t)}</span>
          {activeValue && (
            <Badge variant="secondary" className="h-5 rounded-sm px-1.5 text-[10px]">
              {activeValue.length}
            </Badge>
          )}
          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
        </FilterButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {options.map((option) => {
          const checked = draft.includes(option.value);

          return (
            <DropdownMenuCheckboxItem
              key={option.value}
              checked={checked}
              onSelect={(event) => event.preventDefault()}
              onCheckedChange={(nextChecked) =>
                setDraft((current) => toggleValue(current, option.value, nextChecked === true))
              }
            >
              {option.label}
            </DropdownMenuCheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ConfidenceRangeSlider = ({
  value,
  onValueChange,
}: {
  value: [number, number];
  onValueChange: (value: [number, number]) => void;
}) => (
  <SliderPrimitive.Root
    value={value}
    min={CONFIDENCE_RANGE[0]}
    max={CONFIDENCE_RANGE[1]}
    step={CONFIDENCE_STEP}
    minStepsBetweenThumbs={1}
    onValueChange={(nextValue) => onValueChange([nextValue[0], nextValue[1]] as [number, number])}
    className="relative flex w-full touch-none select-none items-center"
  >
    <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-secondary">
      <SliderPrimitive.Range className="absolute h-full bg-primary" />
    </SliderPrimitive.Track>
    {value.map((thumbValue, index) => (
      <SliderPrimitive.Thumb
        key={index}
        className="block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      />
    ))}
  </SliderPrimitive.Root>
);

const AlertsFilterBar = ({ value, onChange, onClear }: AlertsFilterBarProps) => {
  const { t } = useTranslation("machines");
  const { data: taxonomy } = useGetClassificationTaxonomy();
  const [confidenceOpen, setConfidenceOpen] = useState(false);
  const [draftConfidence, setDraftConfidence] = useState<[number, number]>(
    normalizeConfidencePair(value.min_confidence, value.max_confidence) ?? [0, 1]
  );

  const labelOptions = useMemo(() => {
    const seen = new Set<string>();

    return (
      taxonomy?.families.flatMap((family) =>
        family.members.flatMap((member) => {
          if (seen.has(member.canonical)) return [];
          seen.add(member.canonical);

          return [{ label: member.display_name, value: member.canonical }];
        })
      ) ?? []
    );
  }, [taxonomy]);

  const hasActiveFilters = Boolean(
    normalizeArray(value.type_in)?.length ||
      normalizeArray(value.status_in)?.length ||
      normalizeArray(value.label_in)?.length ||
      normalizeConfidencePair(value.min_confidence, value.max_confidence)
  );

  const handleConfidenceOpenChange = (next: boolean) => {
    if (!next) {
      const nextConfidence = buildConfidencePatch(draftConfidence);

      if (
        nextConfidence.min_confidence !== value.min_confidence ||
        nextConfidence.max_confidence !== value.max_confidence
      ) {
        onChange({
          ...value,
          ...nextConfidence,
        });
      }
    } else {
      setDraftConfidence(
        normalizeConfidencePair(value.min_confidence, value.max_confidence) ?? [0, 1]
      );
    }

    setConfidenceOpen(next);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <MultiSelectFilter
          label={t("alertsFilter.type")}
          options={TYPE_OPTIONS}
          value={value.type_in}
          onChange={(type_in) => onChange({ ...value, type_in })}
        />
        <MultiSelectFilter
          label={t("alertsFilter.status")}
          options={statusOptions(t)}
          value={value.status_in}
          onChange={(status_in) => onChange({ ...value, status_in })}
        />
        <MultiSelectFilter
          label={t("alertsFilter.label")}
          options={labelOptions}
          value={value.label_in}
          onChange={(label_in) => onChange({ ...value, label_in })}
        />
        <Popover open={confidenceOpen} onOpenChange={handleConfidenceOpenChange}>
          <PopoverTrigger asChild>
            <FilterButton active={!!normalizeConfidencePair(value.min_confidence, value.max_confidence)}>
              <span>{getConfidenceTriggerLabel(value, t)}</span>
              <ChevronDown className="h-3.5 w-3.5 shrink-0" />
            </FilterButton>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 space-y-3 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">{t("alertsFilter.confidence.label")}</div>
                <div className="text-xs text-muted-foreground">
                  {formatConfidence(draftConfidence[0])}-{formatConfidence(draftConfidence[1])}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => {
                  setDraftConfidence([0, 1]);
                  onChange({
                    ...value,
                    min_confidence: undefined,
                    max_confidence: undefined,
                  });
                }}
              >
                <X className="h-3.5 w-3.5" />
                {t("alertsFilter.clear")}
              </Button>
            </div>
            <ConfidenceRangeSlider value={draftConfidence} onValueChange={setDraftConfidence} />
            <div className="flex justify-between text-[11px] text-muted-foreground">
              <span>{formatConfidence(CONFIDENCE_RANGE[0])}</span>
              <span>{formatConfidence(CONFIDENCE_RANGE[1])}</span>
            </div>
          </PopoverContent>
        </Popover>
        <div className="inline-flex h-8 items-center rounded-md border border-input bg-background p-0.5">
          {orderingOptions(t).map((option) => {
            const isActive = value.ordering === option.value;

            return (
              <button
                key={option.value}
                type="button"
                className={cn(
                  "rounded-sm px-3 py-1 text-xs font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )}
                onClick={() => {
                  if (option.value !== value.ordering) {
                    onChange({ ...value, ordering: option.value });
                  }
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={onClear}>
            {t("alertsFilter.clearFilters")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default AlertsFilterBar;
